<template>
    <article>
        <h2>Containers metrics</h2>

        <p>
            Each container metric has the <var>container_id</var> label. This is a compound identifier and its
            format varies between container types, e.g.,
            <var>/docker/upbeat_borg</var>, <var>/k8s/namespace-1/pod-2/container-3</var> or <var>/system.slice/nginx.service</var>.
        </p>

        <ul>
            <li><router-link :to="{hash: 'cpu'}">CPU</router-link></li>
            <li><router-link :to="{hash: 'memory'}">Memory</router-link></li>
            <li><router-link :to="{hash: 'disk'}">Disk</router-link></li>
            <li><router-link :to="{hash: 'network'}">Network</router-link></li>
            <li><router-link :to="{hash: 'l7'}">Application layer protocol metrics</router-link></li>
            <li><router-link :to="{hash: 'jvm'}">JVM</router-link></li>
            <li><router-link :to="{hash: 'dotnet'}">.NET runtime</router-link></li>
            <li><router-link :to="{hash: 'logs'}">Logs</router-link></li>
            <li><router-link :to="{hash: 'node'}">Node metrics</router-link></li>
            <li><router-link :to="{hash: 'other'}">Other</router-link></li>
        </ul>

        <h2 id="cpu">CPU</h2>

        <MetricDescription name="container_resources_cpu_limit_cores" type="gauge">
            <template #description>
                CPU limit of the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/scheduler/sched-bwc.txt" target="_blank" rel="noopener noreferrer">CPU</a> cgroup,
                <var>cpu.cfs_quota_us</var> and <var>cpu.cfs_period_us</var> files.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_cpu_usage_seconds_total" type="counter">
            <template #description>
                Total CPU time consumed by the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/cpuacct.txt" target="_blank" rel="noopener noreferrer">CPU accounting</a> cgroup,
               <var>cpuacct.usage</var> file.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_cpu_throttled_seconds_total" type="counter">
            <template #description>
                Total time duration the container has been throttled for.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/scheduler/sched-bwc.txt" target="_blank" rel="noopener noreferrer">CPU</a> cgroup,
                <var>cpu.stat</var> file.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_cpu_delay_seconds_total" type="counter">
            <template #description>
                Total time duration the container has been waiting for a CPU (while being runnable).
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/html/latest/accounting/delay-accounting.html" target="_blank" rel="noopener noreferrer">Delay accounting</a>.
            </template>
        </MetricDescription>

        <h2 id="memory">Memory</h2>

        <MetricDescription name="container_resources_memory_limit_bytes" type="gauge">
            <template #description>
                Memory limit of the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/memory.txt" target="_blank" rel="noopener noreferrer">Memory</a> cgroup, file <var>memory.limit_in_bytes</var>.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_memory_rss_bytes" type="gauge">
            <template #description>
                Amount of physical memory used by the container (doesn't include page cache).
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/memory.txt" target="_blank" rel="noopener noreferrer">Memory</a> cgroup, file <var>memory.stats</var>.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_memory_cache_bytes" type="gauge">
            <template #description>
                Amount of page cache memory allocated by the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/memory.txt" target="_blank" rel="noopener noreferrer">Memory</a> cgroup, file <var>memory.stats</var>.
            </template>
        </MetricDescription>

        <MetricDescription name="container_oom_kills_total" type="counter">
            <template #description>
                Total number of times the container has been terminated by the OOM killer.
            </template>
            <template #source>
                eBPF: <var>tracepoint/oom/mark_victim</var>
            </template>
        </MetricDescription>

        <h2 id="disk">Disk</h2>

        <MetricDescription name="container_resources_disk_delay_seconds_total" type="counter">
            <template #description>
                Total time duration the container has been waiting for I/Os to complete.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/html/latest/accounting/delay-accounting.html" target="_blank" rel="noopener noreferrer">Delay accounting</a>.
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_disk_size_bytes" type="gauge">
            <template #description>
                Total capacity of the volume.
            </template>
            <template #source>
                <a href="https://man7.org/linux/man-pages/man2/statfs.2.html" target="_blank" rel="noopener noreferrer">statfs()</a>
            </template>
            <template #labels>
                <div><var>mount_point</var> - path in the mount namespace of the container</div>
                <div><var>device</var> - device name, e.g., <var>vda</var>, <var>nvme1n1</var></div>
                <div><var>volume</var> - <a href="https://kubernetes.io/docs/concepts/storage/persistent-volumes/" target="_blank" rel="noopener noreferrer">Persistent Volume</a> (Kubernetes only)</div>
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_disk_used_bytes" type="gauge">
            <template #description>
                Used capacity of the volume.
            </template>
            <template #source>
                <a href="https://man7.org/linux/man-pages/man2/statfs.2.html" target="_blank">statfs()</a>
            </template>
            <template #labels>
                <var>mount_point</var>, <var>device</var>, <var>volume</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_disk_(reads|writes)_total" type="counter">
            <template #description>
                Total number of reads or writes completed successfully by the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/blkio-controller.txt" target="_blank">Blkio</a> cgroup,
                <var>blkio.throttle.io_serviced</var> file.
            </template>
            <template #labels>
                <var>mount_point</var>, <var>device</var>, <var>volume</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_resources_disk_(read|written)_bytes_total" type="counter">
            <template #description>
                Total number of bytes read from the disk or written to the disk by the container.
            </template>
            <template #source>
                <a href="https://www.kernel.org/doc/Documentation/cgroup-v1/blkio-controller.txt" target="_blank">Blkio</a> cgroup,
                <var>blkio.throttle.io_service_bytes</var> file.
            </template>
            <template #labels>
                <var>mount_point</var>, <var>device</var>, <var>volume</var>
            </template>
        </MetricDescription>

        <h2 id="network">Network</h2>

        <MetricDescription name="container_net_tcp_listen_info" type="gauge">
            <template #description>
                A TCP listen address of the container.
            </template>
            <template #source>
                eBPF: <var>tracepoint/sock/inet_sock_set_state</var>, <var>/proc/&lt;pid&gt;/net/tcp</var>, <var>/proc/&lt;pid&gt;/net/tcp6</var>
            </template>
            <template #labels>
                <div><var>listen_addr</var> - <var>ip:port</var></div>
                <div><var>proxy</var> - <var>dockerd</var> if the container publishes its ports through the docker daemon. Undefined for other cases.</div>
            </template>
        </MetricDescription>

        <MetricDescription name="container_net_tcp_successful_connects_total" type="counter">
            <template #description>
                Total number of successful TCP connects.
            </template>
            <template #source>
                eBPF: <var>tracepoint/sock/inet_sock_set_state</var>
            </template>
            <template #labels>
                <div><var>destination</var> - IP:port of the connection destination</div>
                <div>
                    <var>actual_destination</var> - actual IP:port of the connection.
                    For example, the container was establishing a connection to port <var>80</var> of a Kubernetes service IP <var>10.96.1.1</var>.
                    The destination address was translated by iptables to a pod IP, e.g., <var>10.40.1.5</var>.
                    In this case <var>actual_destination</var> would be <var>10.40.1.5:80</var>.
                </div>
            </template>
        </MetricDescription>

        <MetricDescription name="container_net_tcp_retransmits_total" type="counter">
            <template #description>
                Total number of retransmitted TCP segments.
                This metric is collected only for outbound TCP connections.
            </template>
            <template #source>
                eBPF: <var>tracepoint/tcp/tcp_retransmit_skb</var>
            </template>
            <template #labels>
                <div><var>destination</var>, <var>actual_destination</var></div>
            </template>
        </MetricDescription>

        <MetricDescription name="container_net_tcp_failed_connects_total" type="counter">
            <template #description>
                Total number of failed TCP connects to a particular endpoint.
                The agent takes into account only TCP failures, so this metric doesn't reflect DNS errors.
            </template>
            <template #source>
                eBPF: <var>tracepoint/sock/inet_sock_set_state</var>
            </template>
            <template #labels>
                <div><var>destination</var> - IP:port of the connection destination</div>
            </template>
        </MetricDescription>

        <MetricDescription name="container_net_tcp_active_connections" type="gauge">
            <template #description>
                Number of active outbound connections between the container and a particular endpoint.
            </template>
            <template #source>
                eBPF: <var>tracepoint/sock/inet_sock_set_state</var>
            </template>
            <template #labels>
                <var>destination</var>, <var>actual_destination</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_net_latency_seconds" type="gauge">
            <template #description>
                Round-trip time between the container and a remote IP.
            </template>
            <template #source>
                The agent measures the round-trip time of an ICMP request sent to an IP address the container is currently working with.
            </template>
            <template #labels>
                <var>destination_ip</var>
            </template>
        </MetricDescription>

        <h2 id="l7">Application layer protocol metrics</h2>

        <MetricDescription name="container_http_requests_total" type="counter">
            <template #description>
                Total number of outbound HTTP requests made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_http_requests_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound HTTP request
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_postgres_queries_total" type="counter">
            <template #description>
                Total number of outbound Postgres queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_postgres_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Postgres query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_redis_queries_total" type="counter">
            <template #description>
                Total number of outbound Redis queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_redis_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Redis query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_memcached_queries_total" type="counter">
            <template #description>
                Total number of outbound Memcached queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_memcached_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Memcached query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_mysql_queries_total" type="counter">
            <template #description>
                Total number of outbound Mysql queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_mysql_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Mysql query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_mongo_queries_total" type="counter">
            <template #description>
                Total number of outbound Mongo queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_mongo_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Mongo query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_kafka_requests_total" type="counter">
            <template #description>
                Total number of outbound Kafka requests made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_kafka_requests_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Kafka request
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_cassandra_queries_total" type="counter">
            <template #description>
                Total number of outbound Cassandra queries made by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_cassandra_queries_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound Cassandra query
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_rabbitmq_messages_total" type="counter">
            <template #description>
                Total number of Rabbitmq messages produced or consumed by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>, <var>method</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_nats_messages_total" type="counter">
            <template #description>
                Total number of NATS messages produced or consumed by the container
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>, <var>method</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dubbo_requests_total" type="counter">
            <template #description>
                Total number of outbound DUBBO requests
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>status</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dubbo_requests_duration_seconds_total" type="counter">
            <template #description>
                Histogram of the response time for each outbound DUBBO request
            </template>
            <template #source>
                eBPF
            </template>
            <template #labels>
                <var>destination_ip</var>, <var>actual_destination</var>, <var>le</var>
            </template>
        </MetricDescription>


        <h2 id="jvm">JVM</h2>

        <p>Each JVM metric has the <var>jvm</var> label which refers to the main class or path to the <var>.jar</var> file.</p>

        <MetricDescription name="container_jvm_info" type="gauge">
            <template #description>
                Meta information about the JVM
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>, <var>java_version</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_jvm_heap_size_bytes" type="gauge">
            <template #description>
                Total heap size in bytes
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_jvm_heap_used_bytes" type="gauge">
            <template #description>
                Used heap size in bytes
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_jvm_gc_time_seconds" type="counter">
            <template #description>
                Time spent in the given JVM garbage collector in seconds
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>, <var>gc</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_jvm_safepoint_time_seconds" type="counter">
            <template #description>
                Time the application has been stopped for safepoint operations in seconds
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_jvm_safepoint_sync_time_seconds" type="counter">
            <template #description>
                Time spent getting to safepoints in seconds
            </template>
            <template #source>
                <var>hsperfdata</var>
            </template>
            <template #labels>
                <var>jvm</var>
            </template>
        </MetricDescription>


        <h2 id="dotnet">.NET runtime</h2>

        <p>
            Each .NET runtime metric has the <var>application</var> label, which allows distinguishing multiple
            applications in the same container.
        </p>

        <MetricDescription name="container_dotnet_info" type="gauge">
            <template #description>
                Meta information about the Common Language Runtime (CLR)
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>, <var>runtime_version</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_memory_allocated_bytes_total" type="counter">
            <template #description>
                The number of bytes allocated
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_exceptions_total" type="counter">
            <template #description>
                The number of exceptions that have occurred
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_memory_heap_size_bytes" type="gauge">
            <template #description>
                Total size of the heap generation in bytes
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>, <var>generation</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_gc_count_total" type="counter">
            <template #description>
                The number of times GC has occurred for the generation
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>, <var>generation</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_heap_fragmentation_percent" type="gauge">
            <template #description>
                The heap fragmentation
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_monitor_lock_contentions_total" type="gauge">
            <template #description>
                The number of times there was contention when trying to take the monitor's lock
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_thread_pool_completed_items_total" type="counter">
            <template #description>
                The number of work items that have been processed in the ThreadPool
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_thread_pool_queue_length" type="gauge">
            <template #description>
                The number of work items that are currently queued to be processed in the ThreadPool
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_dotnet_thread_pool_size" type="gauge">
            <template #description>
                The number of thread pool threads that currently exist in the ThreadPool
            </template>
            <template #source>
                <var>.NET diagnostic port</var>
            </template>
            <template #labels>
                <var>application</var>
            </template>
        </MetricDescription>

        <h2 id="other">Other</h2>

        <MetricDescription name="container_info" type="gauge">
            <template #description>
                Meta information about the container.
            </template>
            <template #source>
                <var>dockerd</var> / <var>containerd</var>
            </template>
            <template #labels>
                <div><var>image</var></div>
            </template>
        </MetricDescription>


        <MetricDescription name="container_restarts_total" type="counter">
            <template #description>
                Number of times the container has been restarted.
            </template>
            <template #source>
                eBPF: <var>tracepoint/task/task_newtask</var> and <var>tracepoint/sched/sched_process_exit</var>
            </template>
        </MetricDescription>

        <MetricDescription name="container_application_type" type="gauge">
            <template #description>
                Type of application running in the container (e.g., memcached, postgres, mysql).
            </template>
            <template #source>
                <var>/proc/&lt;pid&gt;/cmdline</var> of the processes running in the container.
            </template>
            <template #labels>
                <div><var>application_type</var></div>
            </template>
        </MetricDescription>

        <h2 id="logs">Logs</h2>

        <MetricDescription name="container_log_messages_total" type="counter">
            <template #description>
                Number of messages grouped by the automatically extracted repeated patterns.
            </template>
            <template #source>
                The container's log. The following logging methods are supported:
                <ul>
                    <li><var>stdout/stderr</var> streams are captured by Dockerd (json file driver)</li>
                    <li><var>stdout/stderr</var> streams are captured by Containerd (CRI)</li>
                    <li>A container sends its log to Journald</li>
                    <li>A container logs to a file in the <var>/var/log</var> directory</li>
                </ul>
            </template>
            <template #labels>
                <div>
                    <var>source</var> - <var>journald</var>, <var>stdout/stderr</var>, or path to the file in the <var>/var/log</var> directory.
                </div>
                <div><var>level</var> - <var>&lt;unknown | debug | info | warning | error | critical&gt;</var></div>
                <div><var>pattern_hash</var> - the ID of the automatically extracted repeated pattern</div>
                <div><var>sample</var> - a sample message of the group</div>
            </template>
        </MetricDescription>


        <h2 id="node">Node metrics</h2>

        <MetricDescription name="node_resources_cpu_usage_seconds_total" type="counter">
            <template #description>
                Amount of CPU time spent in each mode.
            </template>
            <template #source>
                <var>/proc/stat</var>
            </template>
            <template #labels>
                <div><var>mode</var> - <var>&lt;user | nice | system | idle | iowait | irq | softirq | steal &gt;</var></div>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_cpu_logical_cores" type="gauge">
            <template #description>
                Number of logical CPU cores.
            </template>
            <template #source>
                <var>/proc/stat</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_memory_total_bytes" type="gauge">
            <template #description>
                Total amount of physical memory.
            </template>
            <template #source>
                <var>/proc/meminfo</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_memory_free_bytes" type="gauge">
            <template #description>
                Amount of unassigned memory.
            </template>
            <template #source>
                <var>/proc/meminfo</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_memory_available_bytes" type="gauge">
            <template #description>
                An estimate of how much memory is available for allocations, without swapping.
                Roughly speaking, this is the sum of the <var>free</var> memory and a part of the <var>page cache</var> that can be reclaimed.
                You can learn more about how this estimate is calculated <a href="https://git.kernel.org/pub/scm/linux/kernel/git/torvalds/linux.git/commit/?id=34e431b0ae398fc54ea69ff85ec700722c9da773" target="_blank">here</a>.
            </template>
            <template #source>
                <var>/proc/meminfo</var>
            </template>

        </MetricDescription>

        <MetricDescription name="node_resources_memory_cached_bytes" type="gauge">
            <template #description>
                Amount of memory used as <a href="https://en.wikipedia.org/wiki/Page_cache" target="_blank">page cache</a>.
                The memory used for page cache might be reclaimed on memory pressure. This can increase the number of disk reads.
            </template>
            <template #source>
                <var>/proc/meminfo</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_disk_(reads|writes)_total" type="counter">
            <template #description>
                Total number of reads or writes completed successfully.
                Any disk has the maximum IOPS it can serve. Below are the reference values for the different storage types:
                <v-simple-table dense>
                    <thead><tr><th>Type</th><th>Max IOPS</th></tr></thead>
                    <tbody>
                    <tr><td>Amazon EBS sc1</td><td>250</td></tr>
                    <tr><td>Amazon EBS st1</td><td>500</td></tr>
                    <tr><td>Amazon EBS gp2/gp3</td><td>16,000</td></tr>
                    <tr><td>Amazon EBS io1/io2</td><td>64,000</td></tr>
                    <tr><td>Amazon EBS io2 Block Express</td><td>256,000</td></tr>
                    <tr><td>HDD</td><td>200</td></tr>
                    <tr><td>SATA SSD</td><td>100,000</td></tr>
                    <tr><td>NVMe SSD</td><td>10,000,000</td></tr>
                    </tbody>
                </v-simple-table>
            </template>
            <template #source>
                <var>/proc/diskstats</var>
            </template>
            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_disk_(read|written)_bytes_total" type="counter">
            <template #description>
                Total number of bytes read from the disk or written to the disk respectively.
                In additional to the maximum number of IOPS a disk can serve, there is a throughput limit. For example,
                <v-simple-table dense>
                    <thead><tr><th>Type</th><th>Max throughput</th></tr></thead>
                    <tbody>
                    <tr><td>Amazon EBS sc1</td><td>250 MB/s</td></tr>
                    <tr><td>Amazon EBS st1</td><td>500 MB/s</td></tr>
                    <tr><td>Amazon EBS gp2</td><td>250 MB/s</td></tr>
                    <tr><td>Amazon EBS gp3</td><td>1,000 MB/s</td></tr>
                    <tr><td>Amazon EBS io1/io2</td><td>1,000 MB/s</td></tr>
                    <tr><td>Amazon EBS io2 Block Express</td><td>4,000 MB/s</td></tr>
                    <tr><td>SATA</td><td>600 MB/s</td></tr>
                    <tr><td>SAS</td><td>1,200 MB/s</td></tr>
                    <tr><td>NVMe</td><td>4,000 MB/s</td></tr>
                    </tbody>
                </v-simple-table>
            </template>
            <template #source>
                <var>/proc/diskstats</var>
            </template>
            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_disk_(read|write)_time_seconds_total" type="counter">
            <template #description>
                Total number of seconds spent reading and writing respectively, including queue wait.
                To get the average I/O latency, the sum of these two should be normalized by the number of the executed I/O requests.
                Below is the reference average I/O latency for the different storage types:
                <v-simple-table dense>
                    <thead><tr><th>Type</th><th>Avg latency</th></tr></thead>
                    <tbody>
                    <tr><td>Amazon EBS gp2/gp3/io1/io2</td><td>"single-digit millisecond"</td></tr>
                    <tr><td>Amazon EBS io2 Block Express</td><td>"sub-millisecond"</td></tr>
                    <tr><td>HDD</td><td>2-4ms</td></tr>
                    <tr><td>NVMe SSD</td><td>0.1-0.3ms</td></tr>
                    </tbody>
                </v-simple-table>
            </template>
            <template #source>
                <var>/proc/diskstats</var>
            </template>
            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_resources_disk_io_time_seconds_total" type="counter">
            <template #description>
                Total number of seconds the disk spent doing I/O. It doesn't include queue wait, only service time. <br/>
                E.g., if the derivative of this metric for a minute interval is 60s, this means that the disk was busy 100% of that interval.
            </template>
            <template #source>
                <var>/proc/diskstats</var>
            </template>
            <template #labels>
                <var>device</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_net_received_(bytes|packets)_total" type="counter">
            <template #description>
                Total number of bytes and packets received.
            </template>
            <template #source>
            </template>
            <template #labels>
                <var>interface</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_net_transmitted_(bytes|packets)_total" type="counter">
            <template #description>
                Total number of bytes and packets transmitted.
            </template>
            <template #source>
            </template>
            <template #labels>
                <var>interface</var>
            </template>
        </MetricDescription>


        <MetricDescription name="node_net_interface_up" type="gauge">
            <template #description>
                Status of the interface (0:down, 1:up).
            </template>
            <template #source>
            </template>
            <template #labels>
                <var>interface</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_net_interface_ip" type="gauge">
            <template #description>
                IP address assigned to the interface.
            </template>
            <template #source>
            </template>
            <template #labels>
                <var>interface</var>, <var>ip</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_uptime_seconds" type="gauge">
            <template #description>
                Uptime of the node in seconds.
            </template>
            <template #source>
            </template>
        </MetricDescription>

        <MetricDescription name="node_info" type="gauge">
            <template #description>
                Meta information about the node.
            </template>
            <template #source>
            </template>
            <template #labels>
                <var>hostname</var>, <var>kernel_version</var>, <var>agent_version</var>
            </template>
        </MetricDescription>

        <MetricDescription name="node_cloud_info" type="gauge">
            <template #description>
                Meta information about the cloud instance.
            </template>
            <template #source>
                The agent detects the cloud provider using <a href="https://man7.org/linux/man-pages/man5/sysfs.5.html" target="_blank">sysfs</a>.
                Then it uses cloud-specific metadata services to retrieve additional information about the instance (
                <a href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/ec2-instance-metadata.html" target="_blank">AWS</a>,
                <a href="https://cloud.google.com/compute/docs/metadata/overview" target="_blank">GCP</a>,
                <a href="https://docs.microsoft.com/en-us/azure/virtual-machines/linux/instance-metadata-service?tabs=linux" target="_blank">Azure</a>).
                <div>
                    For unsupported providers, you can use the <var>--provider</var>, <var>--region</var>, and <var>--availability-zone</var>
                    command line arguments of the agent to define the labels manually.
                </div>
            </template>
            <template #labels>
                <div><var>provider</var> - <var>&lt; aws | gcp | azure &gt;</var></div>
                <div><var>account_id</var> -
                    <a href="https://docs.aws.amazon.com/IAM/latest/UserGuide/console_account-alias.html" target="_blank">account_id</a> for AWS,
                    <a href="https://cloud.google.com/storage/docs/projects" target="_blank">project_id</a> for GCP and
                    <a href="https://docs.microsoft.com/en-us/azure/guides/developer/azure-developer-guide#understanding-accounts-subscriptions-and-billing" target="_blank">subscription_id</a> for Azure.</div>
                <div><var>instance_id</var></div>
                <div><var>instance_type</var></div>
                <div><var>instance_life_cycle</var> - <var>&lt; on-demand | spot | preemtible &gt;</var> (always empty for Azure instances)</div>
                <div><var>region</var></div>
                <div><var>availability_zone</var></div>
                <div><var>availability_zone_id</var> - <a href="https://docs.aws.amazon.com/ram/latest/userguide/working-with-az-ids.html" target="_blank">ID</a> of the availability zone (AWS only)</div>
                <div><var>local_ipv4</var></div>
                <div><var>public_ipv4</var></div>
            </template>
        </MetricDescription>
    </article>
</template>

<script>
import MetricDescription from "@/components/MetricDescription";

export default {
    components: {MetricDescription},
}
</script>

<style scoped>
</style>
